import React from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import PropTypes from 'prop-types';

import environments from '../environments';
import { BASENAME, STATIC_DPP_TOP_URL } from '../constants';
import { getAgeFromBirthday } from '../utils';
import { usePrice } from '../utils/price';

const searchLinkText = '検索結果';
const searchLinkUrl = `${STATIC_DPP_TOP_URL}${BASENAME}/search`;
const searchaLinkUrl = `${STATIC_DPP_TOP_URL}${BASENAME}/searcha`;
const planListText = '施設プラン一覧';
const planListUrl = `${STATIC_DPP_TOP_URL}${BASENAME}/plan_list`;
const planDetailText = 'プラン詳細';
const planDetailUrl = `${STATIC_DPP_TOP_URL}${BASENAME}/plan_detail`;


const Head = () => {
  const pcView = window.innerWidth > 768;

  const location = useLocation();
  const { pathname } = location;
  switch (pathname) {

  case '/search': {
    const rtoasterLabel = pcView ? 'rt_search_pc_popup' : 'rt_search_sp_popup';
    return (
      <Helmet>
        <link rel="canonical" href={`${STATIC_DPP_TOP_URL}${BASENAME}${pathname}`} />
        <script type="text/javascript" src="//www.nta.co.jp/shared2015/common/js/air_search_header.js" charSet="utf-8"></script>
        <script type="text/javascript" src="//www.nta.co.jp/shared2015/common/js/air_search_footer.js" charSet="utf-8"></script>
        <script type="text/javascript">{`
            Rtoaster.init("${environments.RTOASTER_CODE}");
            Rtoaster.track();
        `}</script>
        <script type="text/javascript">{`
            Rtoaster.recommendNow("rt_air_emergency", "${rtoasterLabel}");
            Rtoaster.Popup.register("${rtoasterLabel}");
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement":
            [
            {"@type": "ListItem","position": 1,"item": {"@id": "https://www.nta.co.jp/","name": "日本旅行トップ"}}
            ,{"@type": "ListItem","position": 2,"item": {"@id": "https://www.nta.co.jp/kokunai/","name": "国内旅行・国内ツアー"}}
            ,{"@type": "ListItem","position": 3,"item": {"@id": "https://www.nta.co.jp/kokunai/special/air/","name": "航空+宿泊セットプラン"}}
            ,{"@type": "ListItem","position": 4,"item": {"@id": "${searchLinkUrl}","name": "${searchLinkText}"}}
            ]
          }
        `}</script>
      </Helmet>
    );
  }

  case '/searcha': {
    return (
      <Helmet>
        <link rel="canonical" href={`${STATIC_DPP_TOP_URL}${BASENAME}${pathname}`} />
        <script type="text/javascript" src="//www.nta.co.jp/shared2015/common/js/air_search_header.js" charSet="utf-8"></script>
        <script type="text/javascript" src="//www.nta.co.jp/shared2015/common/js/air_search_footer.js" charSet="utf-8"></script>
        <script type="text/javascript">{`
            Rtoaster.init("${environments.RTOASTER_CODE}");
            Rtoaster.track();
        `}</script>
        <script type="text/javascript">{`
            Rtoaster.recommendNow("rt_air_emergency");
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement":
            [
            {"@type": "ListItem","position": 1,"item": {"@id": "https://www.nta.co.jp/","name": "日本旅行トップ"}}
            ,{"@type": "ListItem","position": 2,"item": {"@id": "https://www.nta.co.jp/kokunai/","name": "国内旅行・国内ツアー"}}
            ,{"@type": "ListItem","position": 3,"item": {"@id": "https://www.nta.co.jp/kokunai/special/air/","name": "航空+宿泊セットプラン"}}
            ,{"@type": "ListItem","position": 4,"item": {"@id": "${searchLinkUrl}","name": "${searchLinkText}"}}
            ,{"@type": "ListItem","position": 5,"item": {"@id": "${searchaLinkUrl}","name": "${planListText}"}}
            ]
          }
        `}</script>
      </Helmet>
    );
  }

  case '/plan_list': {
    return (
      <Helmet>
        <script type="text/javascript" src="//www.nta.co.jp/shared2015/common/js/air_search_header.js" charSet="utf-8"></script>
        <script type="text/javascript" src="//www.nta.co.jp/shared2015/common/js/air_search_footer.js" charSet="utf-8"></script>
        <link rel="canonical" href={`${STATIC_DPP_TOP_URL}${BASENAME}${pathname}`} />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement":
            [
            {"@type": "ListItem","position": 1,"item": {"@id": "https://www.nta.co.jp/","name": "日本旅行トップ"}}
            ,{"@type": "ListItem","position": 2,"item": {"@id": "https://www.nta.co.jp/kokunai/","name": "国内旅行・国内ツアー"}}
            ,{"@type": "ListItem","position": 3,"item": {"@id": "https://www.nta.co.jp/kokunai/special/air/","name": "航空+宿泊セットプラン"}}
            ,{"@type": "ListItem","position": 4,"item": {"@id": "${searchLinkUrl}","name": "${searchLinkText}"}}
            ,{"@type": "ListItem","position": 5,"item": {"@id": "${planListUrl}","name": "${planListText}"}}
            ]
          }
        `}</script>
      </Helmet>
    );
  }

  case '/plan_detail': {
    const rtoasterLabel = pcView ? 'rt_details_pc_popup' : 'rt_details_sp_popup';

    return (
      <Helmet>
        <link rel="canonical" href={`${STATIC_DPP_TOP_URL}${BASENAME}${pathname}`} />
        <script type="text/javascript" src="//www.nta.co.jp/shared2015/common/js/air_details_header.js" charSet="utf-8"></script>
        <script type="text/javascript" src="//www.nta.co.jp/shared2015/common/js/air_details_footer.js" charSet="utf-8"></script>
        <script type="text/javascript">{`
            Rtoaster.init("${environments.RTOASTER_CODE}");
            Rtoaster.track();
        `}</script>
        <script type="text/javascript">{`
            Rtoaster.recommendNow("${rtoasterLabel}");
            Rtoaster.Popup.register("${rtoasterLabel}");
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement":
            [
            {"@type": "ListItem","position": 1,"item": {"@id": "https://www.nta.co.jp/","name": "日本旅行トップ"}}
            ,{"@type": "ListItem","position": 2,"item": {"@id": "https://www.nta.co.jp/kokunai/","name": "国内旅行・国内ツアー"}}
            ,{"@type": "ListItem","position": 3,"item": {"@id": "https://www.nta.co.jp/kokunai/special/air/","name": "航空+宿泊セットプラン"}}
            ,{"@type": "ListItem","position": 4,"item": {"@id": "${searchLinkUrl}","name": "${searchLinkText}"}}
            ,{"@type": "ListItem","position": 5,"item": {"@id": "${planListUrl}","name": "${planListText}"}}
            ,{"@type": "ListItem","position": 6,"item": {"@id": "${planDetailUrl}","name": "${planDetailText}"}}
            ]
          }
        `}</script>
      </Helmet>
    );
  }

  case '/entry_confirm': {
    return (
      <Helmet>
        <link rel="canonical" href={`${STATIC_DPP_TOP_URL}${BASENAME}${pathname}`} />
        <script type="text/javascript" src="//www.nta.co.jp/shared2015/common/js/air_details_header.js" charSet="utf-8"></script>
        <script type="text/javascript" src="//www.nta.co.jp/shared2015/common/js/air_details_footer.js" charSet="utf-8"></script>
      </Helmet>
    );
  }

  case '/login': {
    return (
      <Helmet>
        <link rel="canonical" href={`${STATIC_DPP_TOP_URL}${BASENAME}${pathname}`} />
        <script type="text/javascript" src="//www.nta.co.jp/shared2015/common/js/air_search_header_parts3.js" charSet="utf-8"></script>
        <script type="text/javascript" src="//www.nta.co.jp/shared2015/common/js/air_search_footer_parts3.js" charSet="utf-8"></script>
        <script type="text/javascript">{`
          Rtoaster.init("${environments.RTOASTER_CODE}");
          Rtoaster.track();
        `}</script>
        <script type="text/javascript">{`
          Rtoaster.Popup.register("rt_login_popup");
          Rtoaster.recommendNow("rt_login_popup");
        `}</script>
      </Helmet>
    );
  }

  default: {
    return (
      <Helmet>
        <script type="text/javascript" src="//www.nta.co.jp/shared2015/common/js/air_search_header_parts3.js" charSet="utf-8"></script>
        <script type="text/javascript" src="//www.nta.co.jp/shared2015/common/js/air_search_footer_parts3.js" charSet="utf-8"></script>
        <link rel="canonical" href={`${STATIC_DPP_TOP_URL}${BASENAME}${pathname}`} />
      </Helmet>
    );
  }
  }
};

export default Head;

export const FData = ({ action, flightState, plnaDetailState }) => {
  const { plans } = plnaDetailState;
  const plan = plans && plans.length && plans[0].plan;
  const isReady = !!plan;
  if (!isReady) {
    return null;
  }

  const f_data = makeFData(action, flightState, plnaDetailState);

  return (
    <Helmet>
      <script type="text/javascript">{`
        var _f_data = ${JSON.stringify(f_data)};
      `}</script>
    </Helmet>
  );
};

FData.propTypes = {
  action: PropTypes.string.isRequired,
  flightState: PropTypes.object.isRequired,
  plnaDetailState: PropTypes.object.isRequired,
};

const makeFData = (action, flightState, plnaDetailState) => {
  const isPlanDetail = action === 'detail';
  const list = isPlanDetail ? 'プラン詳細' : undefined;
  const step = isPlanDetail ? undefined : '1';
  const number = isPlanDetail ? '1' : undefined;
  const { plans } = plnaDetailState;
  const plan = plans && plans.length && plans[0].plan;
  const { id: planID, roomType, hotel } = plan;
  const hotelID = isPlanDetail ? undefined : hotel.id;
  const roomTypeID = roomType.id;
  const { directionName: regionName, prefectureName, areaName } = hotel.area;
  const {
    selectedOutboundFlight,
    selectedInboundFlight,
    selectedOutboundDate,
    selectedInboundDate,
    searchedFlightsInput,
  } = flightState;
  const { departureAirportCode, airline } = selectedOutboundFlight;
  const productsId = `${planID}-${roomTypeID}-${departureAirportCode}`;
  const brand = airline.toUpperCase();
  const tripStartDate = moment(selectedOutboundDate);
  const tripEndDate = moment(selectedInboundDate);
  const leadtime = tripEndDate.diff(tripStartDate, 'days');
  const leadtimeDisplay = isPlanDetail ? undefined : leadtime.toString();
  const tripStartDateDisplay = tripStartDate.format('YYYYMMDD');
  const tripEndDateDisplay = tripEndDate.format('YYYYMMDD');
  const { people } = searchedFlightsInput;
  const adult = people.adult || 2;
  const childrenA = people.childrenA || 0;
  const childrenB = people.childrenB || 0;
  const peopleNumber = adult + childrenA + childrenB;
  const quantity = isPlanDetail ? undefined : peopleNumber.toString();
  const { totalPrice, pricePerAdult } = usePrice(
    people, selectedOutboundFlight, selectedInboundFlight, plans.map(p => p.plan), selectedOutboundDate
  );

  return {
    action,
    list,
    step,
    productsID: productsId,
    brand,
    category_1: '航空機セットプラン',
    startdateID: tripStartDateDisplay,
    enddateID: tripEndDateDisplay,
    category_3: regionName,
    category_4: prefectureName,
    category_5: areaName,
    variant: departureAirportCode,
    price: pricePerAdult.toString(),
    priceID: totalPrice.toString(),
    quantity,
    number,
    hotelID,
    leadtime: leadtimeDisplay,
    // customerID: userID,
  };
};

export const FDataFromSession = ({ session, step }) => {
  const f_data = makeFDataFromSession(session, step);

  return (
    <Helmet>
      <script type="text/javascript">{`
        var _f_data = ${JSON.stringify(f_data)};
      `}</script>
    </Helmet>
  );
};

FDataFromSession.propTypes = {
  session: PropTypes.object.isRequired,
  step: PropTypes.number.isRequired,
};

const makeFDataFromSession = (session, step) => {
  const { flightSchedule, plans, member } = session;
  const plan = plans[0];
  const { planID, roomTypeID } = plan;
  const { departure } = flightSchedule;
  const { departureAirportCode } = departure;
  const productsId = `${planID}-${roomTypeID}-${departureAirportCode}`;
  const userID = member ? member.userID : undefined;

  return {
    action: 'checkout_option',
    step: step.toString(),
    productsID: productsId,
    category_1: '航空機セットプラン',
    customerID: userID,
  };
};

export const RtoasterOnComplete = ({ session, reservationId }) => {
  const pcView = window.innerWidth > 768;
  const rtoasterLabel1 = pcView ? 'rt_pc_kokunai_complete_01' : 'rt_sp_kokunai_complete_01';
  const rtoasterLabel2 = pcView ? 'rt_pc_kokunai_complete_02' : 'rt_sp_kokunai_complete_02';

  const f_data = makeFDataFromSessionComplete(session, reservationId);

  return (
    <Helmet>
      <script type="text/javascript">{`
        var _f_data = ${JSON.stringify(f_data)};
        Rtoaster.init("${environments.RTOASTER_CODE}");
        Rtoaster.track(_f_data);
        Rtoaster.recommendNow("${rtoasterLabel1}", "${rtoasterLabel2}");
        dataLayer.push({'event': 'priceID_complete'});
      `}</script>
    </Helmet>
  );
};

RtoasterOnComplete.propTypes = {
  session: PropTypes.object.isRequired,
  reservationId: PropTypes.string.isRequired,
};

const makeFDataFromSessionComplete = (session, reservationId) => {
  const {
    flightSchedule, plans, totalAmount, member, accompanies,
  } = session;
  const plan = plans[0];
  const { planID, roomTypeID, regionName, prefectureName, areaName, adultPrice } = plan;
  const { departure, arrival, airline } = flightSchedule;
  const { departureAirportCode, price: departurePrice } = departure;
  const { price: arrivalPrice } = arrival;
  const productsId = `${planID}-${roomTypeID}-${departureAirportCode}`;
  const brand = airline.toUpperCase();
  const tripStartDayString = departure.departureTime.substr(0, 10);
  const tripStartDate = moment(tripStartDayString).format('YYYYMMDD');
  const tripEndDayString = arrival.arrivalTime.substr(0, 10);
  const tripEndDate = moment(tripEndDayString).format('YYYYMMDD');

  const accompanyCount = accompanies ? accompanies.length : 0;
  const peopleNumber = accompanyCount + 1;
  const priceIndividual = departurePrice + arrivalPrice + adultPrice;

  const { userID, sex, birthday } = member;
  const age = getAgeFromBirthday(birthday);
  const sexId = sex === 'MALE' ? '1' : '2';

  return {
    action: 'purchase',
    productsID: productsId,
    reservationID: reservationId,
    tax: '0',
    shipping: '0',
    brand,
    category_1: '航空機セットプラン',
    startdateID: tripStartDate,
    enddateID: tripEndDate,
    category_3: regionName,
    category_4: prefectureName,
    category_5: areaName,
    variant: departureAirportCode,
    price: priceIndividual.toString(),
    priceID: totalAmount.toString(),
    quantity: peopleNumber.toString(),
    number: '1',
    customerID: userID,
    ageID: age.toString(),
    sexID: sexId,
  };
};
